import { UserSettings } from "src/stores/configurationStore"

export interface UserCurrent extends UserBasic {
  settings: { data: UserSettings } | null
  permissions: Record<string, PermissionsForWorkspace> & PermissionsGlobal
}

type PermissionsGlobal = {
  global?: UserPermissions
}

type PermissionsForWorkspace = {
  // Each work domain is an object with its UUID as a key, and the value being an object of user permissions
  work_domain_permissions: Record<string, UserPermissions>
  workspace_permissions: UserPermissions
}

export interface UserPassword {
  current_password: string
  password: string
}

export interface UserBasic {
  id: string // UUID
  email: string
  first_name: string
  last_name: string
  is_active: boolean
  is_superuser: boolean
  roles: string[] // UUIDs
}

export type UserWithAvatar = UserBasic & { avatar?: string | null }

export type UserAvatar = {
  id: string // userID
  path: string
  type: string
}

export interface UserRole {
  id: string // UUID
  name: string // min 1, max 128
  organisation: string // UUID
  service_provider: string // UUID
  workspaces: string[] // UUID
  work_domains: string[] // UUID
}

export const PERMISSION_ACCESS_CREATE = "c"
export const PERMISSION_ACCESS_READ = "r"
export const PERMISSION_ACCESS_UPDATE = "u"
export const PERMISSION_ACCESS_DELETE = "d"
// Currently (15 Jan 2024) no real meaning, only if the user has access to the internal Admin UI
export const PERMISSION_ACCESS_ADMIN_UI = "+"

// Allows for checks for all the user permission and their permutations
type Permutations<T extends string, U extends string = T> = T extends string
  ? T | `${T}${Permutations<Exclude<U, T>>}`
  : never

export type UserPermissionAccess =
  | typeof PERMISSION_ACCESS_CREATE
  | typeof PERMISSION_ACCESS_READ
  | typeof PERMISSION_ACCESS_UPDATE
  | typeof PERMISSION_ACCESS_DELETE
  | typeof PERMISSION_ACCESS_ADMIN_UI

// "C", "R", "U", "D", "+" and all of their combinations and permutations
type UserPermissionAccessCombinations = Permutations<UserPermissionAccess>

export type UserPermissions = {
  [Permission in UserPermissionType]: UserPermissionAccessCombinations
}

export const PERMISSION_ANNOUNCEMENTS = "announcements"
export const PERMISSION_ATTRIBUTES = "attributes"
export const PERMISSION_AUDIT = "audit"
export const PERMISSION_CHANGE_APPROVALS = "change_approvals"
export const PERMISSION_CHANGE_REQUESTS = "change_requests"
export const PERMISSION_ORGANISATIONS = "organisations"
export const PERMISSION_FILES = "files"
export const PERMISSION_GIS = "gis"
export const PERMISSION_HISTORY = "history"
export const PERMISSION_IDENTIFIERS = "identifiers"
export const PERMISSION_ISSUES = "issues"
export const PERMISSION_ITEMS = "items"
export const PERMISSION_LICENSE_ACCOUNTS = "license_accounts"
export const PERMISSION_OAUTH_APPS = "oauth_apps"
export const PERMISSION_OAUTH_TOKENS = "oauth_tokens"
export const PERMISSION_PERFORMED_WORK = "performed_work"
export const PERMISSION_PLANNING = "planning"
export const PERMISSION_PROCEDURES = "procedures"
export const PERMISSION_SERVICE_PROVIDERS = "service_providers"
export const PERMISSION_SPRINGBOARD_ADMIN_UI = "springboard_admin_ui"
export const PERMISSION_USER_ACCOUNTS = "user_accounts"
export const PERMISSION_USER_ROLES = "user_roles"
export const PERMISSION_USER_SETTINGS = "user_settings"
export const PERMISSION_VPC = "vpc"
export const PERMISSION_WORKSPACES = "workspaces"
export const PERMISSION_WORKSPACE_ADMIN_UI = "workspace_admin_ui"
export const PERMISSION_WORK_DOMAINS = "work_domains"
export const PERMISSION_WORK_ORDERS = "work_orders"

const PERMISSIONS_LIST = [
  PERMISSION_ANNOUNCEMENTS,
  PERMISSION_ATTRIBUTES,
  PERMISSION_AUDIT,
  PERMISSION_CHANGE_APPROVALS,
  PERMISSION_CHANGE_REQUESTS,
  PERMISSION_ORGANISATIONS,
  PERMISSION_FILES,
  PERMISSION_GIS,
  PERMISSION_HISTORY,
  PERMISSION_IDENTIFIERS,
  PERMISSION_ISSUES,
  PERMISSION_ITEMS,
  PERMISSION_LICENSE_ACCOUNTS,
  PERMISSION_OAUTH_APPS,
  PERMISSION_OAUTH_TOKENS,
  PERMISSION_PERFORMED_WORK,
  PERMISSION_PLANNING,
  PERMISSION_PROCEDURES,
  PERMISSION_SERVICE_PROVIDERS,
  PERMISSION_SPRINGBOARD_ADMIN_UI,
  PERMISSION_USER_ACCOUNTS,
  PERMISSION_USER_ROLES,
  PERMISSION_USER_SETTINGS,
  PERMISSION_VPC,
  PERMISSION_WORKSPACES,
  PERMISSION_WORKSPACE_ADMIN_UI,
  PERMISSION_WORK_DOMAINS,
  PERMISSION_WORK_ORDERS,
] as const

export type UserPermissionType = (typeof PERMISSIONS_LIST)[number]
